/**
 * The six headline figures of Kautionen & Sicherheiten.
 *
 * THE ROW IS READ LEFT TO RIGHT AS TWO GROUPS, AND THE COLOUR SAYS SO
 * -------------------------------------------------------------------
 * Cards 1-3 are TENANT securities (teal): what tenants placed with us, a
 * liability. Cards 4-5 are LANDLORD securities (violet): what we placed with
 * owners, an asset. Card 6 (amber) is neither — it is an annual EXPENSE. The
 * three colours are a second signal behind the labels, never the only one:
 * every card states its side in words.
 *
 * WHAT THESE NUMBERS ARE NOT
 * --------------------------
 * `Mieterkautionen gesamt` is NOT a cash balance. Most of it is insurance: a
 * guarantee an insurer would pay, money that never moved. The subtext says so on
 * the card, because a six-figure "total" with no qualifier is exactly how K1's
 * "Gebundenes Kapital" came to overstate liquidity on the landlord side.
 *
 * `Jahresprämien total` is OUR annual cost for the guarantees we placed — the
 * landlord-side premium. The tenant payload carries a premium field too, meaning
 * what the tenant pays their own insurer; it is not our expense and deliberately
 * has no card here.
 *
 * No card sums across the two groups. There is no "securities total" and there
 * never should be: adding a liability to an asset produces a number that
 * describes nothing.
 */
import React from "react";
import { Banknote, Landmark, ShieldCheck, Tag, Wallet } from "lucide-react";

import OverviewKpiCard from "../../../components/admin/companyOverview/OverviewKpiCard";
import { chf, chfPrecise, percent } from "../../../components/admin/companyFinance/financeFormat";

/** "CHF 2'400 · 28 %" — the amount already stands in the card's value, so the
 *  subtext carries the share alone. Null when the share cannot be stated. */
function shareText(value) {
  const p = percent(value);
  return p ? `${p} des Gesamtbetrags` : null;
}

export default function DepositsKpiRow({
  tenant,
  landlord,
  tenantError = "",
  landlordError = "",
  tenantLoading = false,
  landlordLoading = false,
  unresolved = null,
  onRetryTenant,
  onRetryLandlord,
}) {
  const tenancyCountText =
    tenant.tenancyCount === null
      ? "Sicherheiten · kein Bargeldbestand"
      : `von ${tenant.tenancyCount} ${
          tenant.tenancyCount === 1 ? "Mietverhältnis" : "Mietverhältnissen"
        } · Sicherheiten, kein Bargeldbestand`;

  const unitsText =
    landlord.unitsWithDeposit === null
      ? "bei Vermietern hinterlegt"
      : `bei Vermietern hinterlegt · ${landlord.unitsWithDeposit} ${
          landlord.unitsWithDeposit === 1 ? "Objekt" : "Objekte"
        }`;

  return (
    <div
      data-testid="deposits-kpi-row"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6"
    >
      <OverviewKpiCard
        testId="deposits-kpi-tenant-total"
        title="Mieterkautionen gesamt"
        value={chf(tenant.total)}
        subtext={tenancyCountText}
        icon={<ShieldCheck className="h-4 w-4" />}
        accent="teal"
        compact
        loading={tenantLoading}
        error={tenantError}
        onRetry={onRetryTenant}
        footer={
          /* Part of the headline has no established type. It stays IN the total —
             the money is real — but a reader must be able to see that a slice of
             it is unclassified rather than discover it later in a table. */
          unresolved ? (
            <span
              data-testid="deposits-kpi-unresolved"
              className="text-[11px] leading-snug text-amber-700 dark:text-amber-300"
            >
              {`davon ${chf(unresolved.amount)} nicht zugeordnet`}
            </span>
          ) : null
        }
      />

      <OverviewKpiCard
        testId="deposits-kpi-tenant-cash"
        title="davon Barkaution"
        value={chf(tenant.cash)}
        subtext={shareText(tenant.cashShare)}
        icon={<Banknote className="h-4 w-4" />}
        accent="teal"
        compact
        loading={tenantLoading}
        error={tenantError}
        onRetry={onRetryTenant}
      />

      <OverviewKpiCard
        testId="deposits-kpi-tenant-insurance"
        title="davon Versicherung"
        value={chf(tenant.insurance)}
        subtext={shareText(tenant.insuranceShare)}
        icon={<ShieldCheck className="h-4 w-4" />}
        accent="teal"
        compact
        loading={tenantLoading}
        error={tenantError}
        onRetry={onRetryTenant}
      />

      <OverviewKpiCard
        testId="deposits-kpi-landlord-total"
        title="Sicherheitsleistungen bei Vermietern"
        value={chf(landlord.total)}
        subtext={unitsText}
        icon={<Landmark className="h-4 w-4" />}
        accent="violet"
        compact
        loading={landlordLoading}
        error={landlordError}
        onRetry={onRetryLandlord}
      />

      <OverviewKpiCard
        testId="deposits-kpi-landlord-insurance"
        title="davon Versicherung"
        value={chf(landlord.insurance)}
        subtext={shareText(landlord.insuranceShare)}
        icon={<Wallet className="h-4 w-4" />}
        accent="violet"
        compact
        loading={landlordLoading}
        error={landlordError}
        onRetry={onRetryLandlord}
      />

      <OverviewKpiCard
        testId="deposits-kpi-landlord-premium"
        title="Jahresprämien total"
        /* Rappen, deliberately. A premium is an invoiced amount and the cost
           breakdown divides it by twelve; rounding it to francs here would put
           this card and that figure permanently a few centimes apart. */
        value={chfPrecise(landlord.annualPremium)}
        subtext="Versicherungsprämien p.a. · nicht Teil der Sicherheitsbeträge"
        icon={<Tag className="h-4 w-4" />}
        accent="amber"
        compact
        loading={landlordLoading}
        error={landlordError}
        onRetry={onRetryLandlord}
      />
    </div>
  );
}
